import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/common';
import { useResource } from '@/hooks';
import { ApiRequestError, errorMessage } from '@/lib/api-client';
import { logger } from '@/lib/logger';
import { outbox, type TranscriptItem, transcriptOutbox } from '@/lib/outbox';
import type { Transcript, User } from '@/types/api';
import type { DictationSession } from '../../dictation';
import { failureKind } from '../../sync/syncOutbox';
import { transcriptsApi } from '../api';

/** Wait this long after the last change before sending the text again. */
const SEND_DELAY_MS = 800;

function upsert(list: readonly Transcript[], t: Transcript): Transcript[] {
  return list.some((x) => x.clientId === t.clientId)
    ? list.map((x) => (x.clientId === t.clientId ? t : x))
    : [...list, t];
}

/** A queued copy, shown on top of (or instead of) the server's version. */
function withQueued(server: Transcript | undefined, item: TranscriptItem): Transcript {
  return {
    id: server?.id ?? `local-${item.clientId}`,
    clientId: item.clientId,
    patientId: item.patientId,
    visitId: server?.visitId ?? null,
    lang: item.lang,
    text: item.text,
    createdBy: server?.createdBy ?? { id: item.authorId, name: item.authorName },
    createdAt: server?.createdAt ?? item.recordedAt,
    editedBy: server?.editedBy ?? null,
    editedAt: server?.editedAt ?? null,
    local: true,
  };
}

/**
 * The patient's dictations. The device's copy of the one being dictated is
 * stored on the device first (so nothing is lost offline), then sent.
 */
export function useChartTranscripts(
  patientId: string,
  user: User | null,
  dentistId: string | null
) {
  const { t } = useTranslation();
  const toast = useToast();
  const server = useResource(() => transcriptsApi.list(patientId), patientId);
  const { setData } = server;
  const [queued, setQueued] = useState<TranscriptItem[]>([]);
  const userId = user?.id ?? null;

  useEffect(() => {
    let alive = true;
    const load = () =>
      transcriptOutbox.forPatient(patientId).then((all) => {
        if (alive) setQueued(all.filter((i) => i.authorId === userId));
      });
    void load();
    const off = outbox.subscribe(() => void load());
    return () => {
      alive = false;
      off();
    };
  }, [patientId, userId]);

  /** Sends the latest stored copy of the dictation (never an older one). */
  const send = useCallback(
    async (clientId: string) => {
      try {
        const saved = await transcriptOutbox.sendLatest(clientId, transcriptsApi.saveItem);
        if (saved) setData((list) => upsert(list ?? [], saved));
      } catch (err) {
        // A temporary failure stays queued; the outbox sync sends it later.
        if (failureKind(err) === 'reject') {
          logger.warn('transcript refused', err);
          await transcriptOutbox.markFailed(clientId, errorMessage(err));
        }
      }
    },
    [setData]
  );

  // Device writes go one after another (an older text never lands after a
  // newer one) and never wait for the network.
  const stored = useRef<Promise<void>>(Promise.resolve());
  const pending = useRef<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const flush = useCallback(() => {
    window.clearTimeout(timer.current);
    const clientId = pending.current;
    pending.current = null;
    if (clientId) void stored.current.then(() => send(clientId));
  }, [send]);

  const record = useCallback(
    (session: DictationSession) => {
      if (!user) return;
      const item: TranscriptItem = {
        clientId: session.clientId,
        patientId,
        dentistId,
        lang: session.lang,
        text: session.lines.join('\n'),
        recordedAt: session.startedAt,
        authorId: user.id,
        authorName: user.name,
        queuedAt: new Date().toISOString(),
      };
      stored.current = stored.current
        .then(() => transcriptOutbox.put(item))
        .catch((err) => logger.warn('dictation not stored on the device', err));
      pending.current = session.clientId;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(flush, SEND_DELAY_MS);
    },
    [user, patientId, dentistId, flush]
  );

  // Leaving the chart sends what is still waiting.
  useEffect(() => flush, [flush]);

  const edit = useCallback(
    async (transcript: Transcript, text: string): Promise<boolean> => {
      try {
        const saved = await transcriptsApi.edit(transcript.id, text);
        setData((list) => upsert(list ?? [], saved));
        return true;
      } catch (err) {
        toast.show(
          err instanceof ApiRequestError && err.isNetwork
            ? t('chart.toast.needsOnline')
            : errorMessage(err),
          'error'
        );
        return false;
      }
    },
    [setData, toast, t]
  );

  const transcripts = useMemo(() => {
    let list = [...(server.data ?? [])];
    for (const item of queued) {
      const saved = list.find((x) => x.clientId === item.clientId);
      // A hand edit wins over the device's copy (the server keeps it too).
      if (!saved?.editedAt) list = upsert(list, withQueued(saved, item));
    }
    return list.filter((x) => x.text.trim());
  }, [server.data, queued]);

  return { transcripts, loading: server.loading && !server.data, record, edit };
}
